import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { format, setHours, setMinutes, addMinutes, isBefore } from "date-fns";
import { bookingConfig } from "../../../../../data/booking/bookingConfig";
import { ArrowLeft, Clock } from "lucide-react";
import "./TimeSelector.css";

const TimeSelector = ({ selectedDate, onSelectTime, onBack }) => {
  const [selectedDuration, setSelectedDuration] = useState(60);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);

  const selectorRef = useRef(null);
  const headerRef = useRef(null);
  const durationsRef = useRef(null);
  const slotsRef = useRef(null);
  const selectedRef = useRef(null);

  // Initial fade-in animation
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      tl.from(headerRef.current, {
        opacity: 0,
        y: -20,
        duration: 0.5,
        ease: "power3.out",
      }).from(
        durationsRef.current,
        {
          opacity: 0,
          y: 20,
          duration: 0.5,
          ease: "power3.out",
        },
        "-=0.3",
      );
    }, selectorRef);

    return () => ctx.revert();
  }, []);

  // Animate time slots when they appear
  useEffect(() => {
    if (availableSlots.length > 0 && slotsRef.current) {
      const slots = slotsRef.current.querySelectorAll(".time-selector__slot");
      gsap.fromTo(
        slots,
        { opacity: 0, scale: 0.8, y: 20 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.03,
          ease: "back.out(1.7)",
        },
      );
    }
  }, [availableSlots]);

  // Animate selected time display
  useEffect(() => {
    if (selectedTime && selectedRef.current) {
      gsap.fromTo(
        selectedRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" },
      );
    }
  }, [selectedTime]);

  useEffect(() => {
    if (selectedDate) {
      fetchBookedSlots();
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate) {
      generateTimeSlots();
    }
  }, [selectedDate, selectedDuration, bookedSlots]);

  const fetchBookedSlots = async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await fetch(`/api/booked-slots?date=${dateStr}`);
      const data = await response.json();
      setBookedSlots(data.bookedSlots || []);
    } catch (err) {
      console.error("Failed to fetch booked slots:", err);
      setBookedSlots([]);
    }
  };

  const generateTimeSlots = () => {
    const dayName = format(selectedDate, "EEEE").toLowerCase();
    const businessHours = bookingConfig.businessHours[dayName];

    if (!businessHours) {
      setAvailableSlots([]);
      return;
    }

    const slots = [];
    const { start, end } = businessHours;
    const now = new Date();
    const isToday =
      format(selectedDate, "yyyy-MM-dd") === format(now, "yyyy-MM-dd");

    let currentTime = setMinutes(setHours(selectedDate, start), 0);
    const endTime = setMinutes(setHours(selectedDate, end), 0);

    while (isBefore(currentTime, endTime)) {
      const slotEnd = addMinutes(
        currentTime,
        selectedDuration + bookingConfig.bufferBetweenCalls,
      );

      if (
        isBefore(slotEnd, endTime) ||
        format(slotEnd, "HH:mm") === format(endTime, "HH:mm")
      ) {
        if (!isToday || isBefore(now, currentTime)) {
          const timeStr = format(currentTime, "HH:mm");
          if (!bookedSlots.includes(timeStr)) {
            slots.push({
              time: currentTime,
              label: format(currentTime, "h:mm a"),
            });
          }
        }
      }

      currentTime = addMinutes(currentTime, bookingConfig.slotInterval);
    }

    setAvailableSlots(slots);
  };

  const handleTimeSelect = (slot) => {
    setSelectedTime(slot);
  };

  const handleContinue = () => {
    if (selectedTime) {
      gsap.to(selectorRef.current, {
        opacity: 0,
        y: -20,
        duration: 0.3,
        ease: "power2.in",
        onComplete: () => onSelectTime(selectedTime.time, selectedDuration),
      });
    }
  };

  return (
    <div className="time-selector" ref={selectorRef}>
      <button className="time-selector__back" onClick={onBack}>
        <ArrowLeft size={20} />
        Back to Calendar
      </button>

      <div className="time-selector__header" ref={headerRef}>
        <h2 className="time-selector__title">Select Time & Duration</h2>
        <p className="time-selector__subtitle">
          {format(selectedDate, "EEEE, MMMM do, yyyy")}
        </p>
      </div>

      <div className="time-selector__durations" ref={durationsRef}>
        <div className="time-selector__durations-label">Call Duration:</div>
        <div className="time-selector__duration-options">
          {bookingConfig.durations.map((duration) => (
            <button
              key={duration.value}
              className={`time-selector__duration-btn ${
                selectedDuration === duration.value
                  ? "time-selector__duration-btn--active"
                  : ""
              }`}
              onClick={() => setSelectedDuration(duration.value)}
            >
              <Clock size={18} />
              {duration.label}
            </button>
          ))}
        </div>
      </div>

      {availableSlots.length > 0 ? (
        <div className="time-selector__slots" ref={slotsRef}>
          {availableSlots.map((slot, index) => (
            <button
              key={index}
              className={`time-selector__slot ${
                selectedTime?.label === slot.label
                  ? "time-selector__slot--selected"
                  : ""
              }`}
              onClick={() => handleTimeSelect(slot)}
            >
              {slot.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="time-selector__no-slots">
          <p>
            No available time slots for this date. Please select another day.
          </p>
        </div>
      )}

      {selectedTime && (
        <div className="time-selector__selected" ref={selectedRef}>
          <div className="time-selector__selected-info">
            Selected: <strong>{selectedTime.label}</strong> ({selectedDuration}{" "}
            minutes)
          </div>
          <button
            className="time-selector__continue-btn"
            onClick={handleContinue}
          >
            Continue to Details
          </button>
        </div>
      )}
    </div>
  );
};

export default TimeSelector;
